import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import EntityTable from '../components/Listing/EntityTable';
import InsurancePatientsModal from '../components/Modal/InsurancePatientsModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH, faCirclePlus, faEnvelope, faGlobe, faUsers } from '@fortawesome/free-solid-svg-icons';
import InputText from '../components/Form/Fields/InputText';
import { API_BASE_URL } from '../api';

export default function Insurances() {
    const { t } = useTranslation();
    const { setTitle, setTitleIcon } = usePageTitle();
    const tableRef = useRef(null);
    const [viewPatientsInsurance, setViewPatientsInsurance] = useState(null);

    useEffect(() => {
        setTitle(`${t('Insurances')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">${t('insurances.subtitle')}</span>`);
        document.title = t('Insurances');
        setTitleIcon('ti-shield-heart');
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    useBreadcrumbSetup([{ label: t('Insurances'), url: '/insurances' }]);

    const columns = [
        {
            Header: '#',
            accessor: 'id',
            sortable: true,
            width: 50,
            Cell: (row) => (
                <span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af' }}>{row.id}</span>
            ),
        },
        {
            Header: t('insurances.column.logo'),
            accessor: 'logoUrl',
            width: 80,
            Cell: (row) => row.logoUrl
                ? (
                    <img
                        src={row.logoUrl}
                        alt={row.name}
                        style={{ height: 28, maxWidth: 60, objectFit: 'contain', borderRadius: 4 }}
                    />
                )
                : <span className="cell-muted">—</span>,
        },
        {
            Header: t('insurances.column.name'),
            accessor: 'name',
            sortable: true,
            filterable: { component: InputText },
            Cell: (row) => (
                <div>
                    <span className="cell-name">{row.name}</span>
                    {row.description && <span className="cell-sub">{row.description}</span>}
                </div>
            ),
        },
        {
            Header: t('Email'),
            accessor: 'email',
            Cell: (row) => row.email
                ? (
                    <a href={`mailto:${row.email}`} style={{ fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none' }}>
                        <FontAwesomeIcon icon={faEnvelope} style={{ color: '#9ca3af', fontSize: 11 }} />
                        {row.email}
                    </a>
                )
                : <span className="cell-muted">—</span>,
        },
        {
            Header: t('insurances.column.website'),
            accessor: 'website',
            Cell: (row) => row.website
                ? (
                    <a href={row.website} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none' }}>
                        <FontAwesomeIcon icon={faGlobe} style={{ color: '#9ca3af', fontSize: 11 }} />
                        {row.website.replace(/^https?:\/\//, '')}
                    </a>
                )
                : <span className="cell-muted">—</span>,
        },
        {
            Header: t('Patients'),
            accessor: 'patientCount',
            className: 'text-center',
            width: 100,
            Cell: (row) => (
                <span style={{
                    display: 'inline-block', fontSize: 13, fontWeight: 600,
                    padding: '2px 10px', borderRadius: 20,
                    background: row.patientCount > 0 ? '#eff6ff' : '#f9fafb',
                    color: row.patientCount > 0 ? '#1d4ed8' : '#9ca3af',
                }}>
                    {row.patientCount}
                </span>
            ),
        },
        {
            Header: '',
            accessor: 'actions',
            className: 'col-actions',
            Cell: (row) => (
                <div className="cell-dropdown">
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                            <button className="dropdown-item" onClick={() => setViewPatientsInsurance(row)}>
                                <FontAwesomeIcon icon={faUsers} className="me-2 text-muted" />
                                {t('insurances.action.view_patients')}
                            </button>
                        </li>
                    </ul>
                </div>
            ),
        },
    ];

    // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
    // — reuses the actions column's own dropdown so both views offer identical actions.
    const actionsColumn = columns.find((c) => c.accessor === 'actions');
    const renderInsuranceCard = (row) => (
        <div className="card h-100 shadow-sm">
            <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                <div className="d-flex align-items-center gap-3">
                    {row.logoUrl
                        ? <img src={row.logoUrl} alt={row.name} style={{ height: 28, maxWidth: 60, objectFit: 'contain', borderRadius: 4 }} />
                        : null}
                    <div style={{ minWidth: 0 }}>
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: '#111827', display: 'block' }}>{row.name}</span>
                        {row.description && (
                            <span style={{ fontSize: 11.5, color: '#9ca3af', display: 'block' }}>{row.description}</span>
                        )}
                    </div>
                </div>

                {row.email && (
                    <span style={{ fontSize: 11.5, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <FontAwesomeIcon icon={faEnvelope} style={{ color: '#9ca3af', fontSize: 10 }} />
                        {row.email}
                    </span>
                )}
                {row.website && (
                    <span style={{ fontSize: 11.5, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <FontAwesomeIcon icon={faGlobe} style={{ color: '#9ca3af', fontSize: 10 }} />
                        {row.website.replace(/^https?:\/\//, '')}
                    </span>
                )}

                <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
                    <span style={{
                        fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20,
                        background: row.patientCount > 0 ? '#eff6ff' : '#f9fafb',
                        color: row.patientCount > 0 ? '#1d4ed8' : '#9ca3af',
                    }}>
                        {row.patientCount} {t('Patients')}
                    </span>
                    {actionsColumn?.Cell(row)}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <EntityTable
                ref={tableRef}
                endpoint={`${API_BASE_URL}/api/v1/healthinsurances`}
                columns={columns}
                defaultSort="id"
                defaultDirection="asc"
                pageSize={20}
                emptyMessage={t('insurances.empty')}
                searchable={true}
                searchPlaceholder={t('insurances.search_placeholder')}
                multiSelect={true}
                enableCardView={true}
                renderCard={renderInsuranceCard}
            />
            <InsurancePatientsModal
                show={!!viewPatientsInsurance}
                insurance={viewPatientsInsurance}
                onClose={() => setViewPatientsInsurance(null)}
            />
        </>
    );
}
